import { Component } from 'react'

class ErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null, errorInfo: null }
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true }
    }

    componentDidCatch(error, errorInfo) {
        // Log error details to localStorage for debugging mobile crashes
        const errorLog = {
            timestamp: new Date().toISOString(),
            message: error?.message || 'Unknown error',
            stack: error?.stack || '',
            componentStack: errorInfo?.componentStack || '',
            userAgent: navigator.userAgent,
            url: window.location.href,
        }

        console.error('React Error Boundary caught an error:', error, errorInfo)

        // Save to localStorage (survives page reloads)
        try {
            const existingLogs = JSON.parse(localStorage.getItem('react_error_logs') || '[]')
            existingLogs.push(errorLog)
            // Keep only last 10 errors
            if (existingLogs.length > 10) {
                existingLogs.shift()
            }
            localStorage.setItem('react_error_logs', JSON.stringify(existingLogs))
        } catch (storageError) {
            console.error('Failed to save error log:', storageError)
        }

        this.setState({ error, errorInfo })
    }

    clearError = () => {
        this.setState({ hasError: false, error: null, errorInfo: null })
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-4">
                    <div className="max-w-lg rounded-lg border border-red-200 bg-white p-6 shadow-lg">
                        <h1 className="mb-4 text-2xl font-bold text-red-600">Something went wrong</h1>
                        <p className="mb-4 text-slate-700">
                            The app encountered an error. This has been logged for debugging.
                        </p>

                        <details className="mb-4 rounded bg-slate-50 p-3 text-sm">
                            <summary className="cursor-pointer font-semibold text-slate-700">
                                Error Details
                            </summary>
                            <div className="mt-2 space-y-2">
                                <div>
                                    <strong>Message:</strong>
                                    <pre className="mt-1 overflow-x-auto text-xs text-red-600">
                                        {this.state.error?.message}
                                    </pre>
                                </div>
                                <div>
                                    <strong>Stack:</strong>
                                    <pre className="mt-1 max-h-40 overflow-auto text-xs text-slate-600">
                                        {this.state.error?.stack}
                                    </pre>
                                </div>
                            </div>
                        </details>

                        <div className="flex gap-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
                            >
                                Reload Page
                            </button>
                            <button
                                onClick={this.clearError}
                                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-700 hover:bg-slate-50"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
